import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Search } from "lucide-react";
import Carousel from "../components/carousel/logincarousel";
import loginbanner from "../assets/images/login-banner.png";
import loginbannertwo from "../assets/images/login-banner-2.png";
import loginbannerthree from "../assets/images/login-banner-3.png";
import loginbannerfour from "../assets/images/login-banner-4.png";
import loginbannersm from "../assets/images/login-banner-sm.png";
import bannerlayout from "../assets/images/banner-layout.png";
import { getStoredToken } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const slides = [loginbanner, loginbannertwo, loginbannerthree, loginbannerfour];
const slidessm = [loginbannersm, loginbannersm, loginbannersm, loginbannersm];

// Interest.category is an enum on the API side; fall back to a readable version for any new value
const CATEGORY_LABELS = {
  creative_and_artistic: "Creative & Artistic",
  entertainment_and_pop_culture: "Entertainment & Pop Culture",
};
const formatCategory = (category) =>
  CATEGORY_LABELS[category] || category.replace(/_and_/g, " & ").replace(/_/g, " ");

// Shown once, right after email signup verification. Skip and Save both continue to /home.
const SelectInterests = () => {
  const navigate = useNavigate();
  const [interests, setInterests] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/interest/list`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setInterests(data.result?.interests || []);
      })
      .catch((err) => console.error("Failed to fetch interests:", err))
      .finally(() => setLoading(false));
  }, []);

  // The list API has no search param, so filter tags locally and hide categories with no match
  const filteredInterests = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return interests;
    return interests
      .map((section) => ({
        ...section,
        tags: section.tags.filter((tag) => tag.toLowerCase().includes(query)),
      }))
      .filter((section) => section.tags.length > 0);
  }, [interests, search]);

  const toggleInterest = (tag) => {
    setSelected((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const goHome = () => navigate("/home", { replace: true });

  const saveInterests = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/userinterest/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: getStoredToken() },
        body: JSON.stringify({ tags: selected }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.result?.message || "Failed to save interests");
      }
      toast.success("Interests saved");
      goHome();
    } catch (err) {
      console.error("Failed to save interests:", err);
      toast.error("Failed to save interests. Please try again or skip.");
      setSaving(false);
    }
  };

  return (
    <div className="login">
      <div className="container mx-auto flex">
        {/* Left side image */}
        <div className="hidden lg:block lg:w-1/2">
          <div className="carousel-size pb-3 pt-3 ">
            <Carousel slides={slides} />
            <img src={bannerlayout} alt="Overlay" className="layout" />
          </div>
          <div className="copy-rights py-6 text-center text-sm text-[#095228]">
            © 2025 Nyawoo. All Rights Reserved
          </div>
        </div>

        {/* Right side interest selection */}
        <div className="w-full lg:w-1/2 login-content flex flex-col">
          <div className="carousel-d-none-sm md:block lg:hidden ">
            <div className="carousel-size pb-3 pt-3 ">
              <Carousel slides={slides} />
              <img src={bannerlayout} alt="Overlay" className="layout" />
            </div>
          </div>
          <div className="lg:hidden md:hidden">
            <div className="carousel-size pb-3 pt-3 ">
              <Carousel slides={slidessm} />
              <img src={bannerlayout} alt="Overlay" className="layout" />
            </div>
          </div>

          <div className="intrest px-4 md:px-8 py-8">
            <div className="flex flex-wrap justify-between items-center gap-4 title">
              <div className="flex items-center space-x-2">
                <h2>Select Your Interests</h2>
                <span className="bg-green-900 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {String(selected.length).padStart(2, "0")}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  className="bg-[#161F28] text-white save-cta disabled:opacity-50"
                  onClick={saveInterests}
                  disabled={saving || selected.length === 0}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  className="border border-gray-400 reset-cta"
                  onClick={goHome}
                  disabled={saving}
                >
                  SKIP
                </button>
              </div>
            </div>
            <hr className="my-5 bg-[#D9D9D9] p-[1px]" />

            {/* Search bar */}
            <div className="relative mb-6 search">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full border bg-[#F0F0F0] border-gray-300 rounded-full px-4 py-2 pl-10 focus:outline-none focus:ring-1 focus:ring-green-800"
                placeholder="Search Interests"
              />
              <Search className="absolute left-3 top-2.5 text-[#B8B8B8] search-cta" size={18} />
            </div>

            {/* Interest categories */}
            {loading ? (
              <p className="text-sm text-gray-500">Loading interests...</p>
            ) : filteredInterests.length === 0 ? (
              <p className="text-sm text-gray-500">No interests found</p>
            ) : (
              <div className="space-y-6">
                {filteredInterests.map((section) => (
                  <div key={section.id} className="mb-8">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="bg-flash-icon" />
                      <h3 className="sec-catagory">{formatCategory(section.category)}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {section.tags.map((tag) => {
                        const isSelected = selected.includes(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => toggleInterest(tag)}
                            className={`px-3 py-1 text-sm rounded-full border ${
                              isSelected
                                ? "bg-[#39B54A] text-[#FFFFFF] border-[#39B54A] selected-intrest"
                                : "bg-[#EBF8ED] text-[#000000] hover:bg-gray-200 unselected-intrest"
                            }`}
                          >
                            {tag} <span>×</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectInterests;
